import { useEffect } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiDetailPageHeader,
  MiInputTextbox,
} from "@miplatform/components";
import {
  DAYS_ORDER,
  ENTITY_NAMES,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { mdiChevronLeft } from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useManageOrderTemplateCapacityViewModel } from "./useManageOrderTemplateCapacityViewModel";

const TABLE_MIN_WIDTH = 300;
const INPUT_WIDTH = 150;

export function ManageOrderTemplateCapacity() {
  const { classes } = useStyles();

  const {
    methods,
    isLoading,
    handleReset,
    getOrderTemplateCapacities,
    handleSubmit,
  } = useManageOrderTemplateCapacityViewModel();

  useEffect(() => {
    getOrderTemplateCapacities();
  }, []);

  const { fields: orderTemplates } = useFieldArray({
    control: methods.control,
    name: "orderTemplates",
  });

  return (
    <Box className={classes.container}>
      <MiDetailPageHeader
        title={`${ENTITY_NAMES.ORDER_TEMPLATE} Capacity`}
        variant="underlined"
        titleIcon={mdiChevronLeft}
        loading={isLoading}
      />

      <MiBox className={classes.contentContainer} overflow="auto" padding={16}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BODY1}
          fontWeight="600"
          sx={{ mb: 1 }}
        >
          Capacity by {ENTITY_NAMES.ORDER_TEMPLATE.toLowerCase()}
        </Typography>
        <Typography variant={TYPOGRAPHY_VARIANTS.BODY2} sx={{ mb: 6 }}>
          Define your capacity for each type of{" "}
          {ENTITY_NAMES.ORDER_TEMPLATE.toLowerCase()} within your system by day
          of week
        </Typography>

        <form>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table sx={{ minWidth: TABLE_MIN_WIDTH }} size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography
                      variant={TYPOGRAPHY_VARIANTS.BODY1}
                      fontWeight="600"
                    >
                      Day of week
                    </Typography>
                  </TableCell>
                  {orderTemplates.map((orderTemplate) => (
                    <TableCell
                      key={orderTemplate.orderTemplateId}
                      sx={{ fontWeight: 500 }}
                    >
                      <Typography
                        variant={TYPOGRAPHY_VARIANTS.BODY1}
                        fontWeight="600"
                        width={INPUT_WIDTH}
                      >
                        {orderTemplate.orderTemplateName}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {DAYS_ORDER.map((day, dayIndex) => (
                  <TableRow key={day}>
                    <TableCell component="th" scope="row">
                      <Typography
                        variant={TYPOGRAPHY_VARIANTS.BODY1}
                        sx={{ paddingBottom: 8 }}
                      >
                        {day}
                      </Typography>
                    </TableCell>
                    {orderTemplates.map((orderTemplate, orderTemplateIndex) => (
                      <TableCell
                        key={`${orderTemplate.orderTemplateId}-${day}`}
                      >
                        <Controller
                          name={`orderTemplates.${orderTemplateIndex}.capacities.${dayIndex}.amount`}
                          control={methods.control}
                          render={({ field, fieldState: { error } }) => (
                            <MiInputTextbox
                              {...field}
                              type="number"
                              name={`capacity-${orderTemplate.orderTemplateId}-${day}`}
                              label="Capacity"
                              error={error}
                              style={{ width: INPUT_WIDTH }}
                              helperText={" "}
                            />
                          )}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </form>
      </MiBox>
      <Footer
        show={methods.formState.isDirty}
        handleSaveClick={methods.handleSubmit(handleSubmit)}
        handleCancelClick={handleReset}
        disableSave={!methods.formState.isValid || isLoading}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "inherit",
  },
  contentContainer: {
    height: "inherit",
    overflow: "auto",
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(6),
  },
  tableContainer: {
    boxShadow: 1,
    width: "min-content",
    "& .MuiTableCell-root": {
      borderBottom: "none",
      backgroundColor: theme.palette.grey.light,
    },
  },
}));
